import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

import { useMutation } from "@tanstack/react-query";

import { ApiCaller, env } from "@virtual-brain-lib/frontend-api";
import type {
	IntegrationCallbackEvent,
	IntegrationName,
	IntegrationProvider,
} from "@virtual-brain-lib/types";

export type IntegrationPopupStatus = "idle" | "waiting" | "closed" | "connected";

type IntegrationCallbackSuccessEvent = Extract<IntegrationCallbackEvent, { status: "success" }>;

interface UseIntegrationPopupParams {
	onSuccess?: (event: IntegrationCallbackSuccessEvent) => void;
}

const POPUP_CLOSED_CHECK_INTERVAL = 500;

export const useIntegrationPopup = ({ onSuccess }: UseIntegrationPopupParams = {}) => {
	const popupRef = useRef<Window | null>(null);
	const [popupStatus, setPopupStatus] = useState<IntegrationPopupStatus>("idle");

	const { mutate: openIntegrationPopup } = useMutation({
		mutationFn: async ({
			provider,
			name,
		}: {
			provider: IntegrationProvider;
			name: IntegrationName;
		}) => ApiCaller.initIntegration(provider, name),
		onSuccess({ url }) {
			popupRef.current = window.open(
				url,
				"_blank",
				"location=yes,height=570,width=520,scrollbars=yes,status=yes",
			);
			setPopupStatus("waiting");
		},
		onError() {
			toast.error("Could not contact the provider. Please try again later.");
		},
	});

	useEffect(() => {
		const handlePostMessage = (event: MessageEvent<IntegrationCallbackEvent>) => {
			if (event.origin !== env.apiUrl || event.data.event !== "integration-callback") {
				return;
			}

			const isOpenedHere = popupRef.current !== null;

			popupRef.current = null;

			if (event.data.status === "error") {
				if (isOpenedHere) {
					setPopupStatus("closed");
					toast.error(
						"Could not connect to the integration provider. Please try again later.",
					);
				}

				return;
			}

			if (isOpenedHere) {
				setPopupStatus("connected");
			}

			onSuccess?.(event.data);
		};

		window.addEventListener("message", handlePostMessage);

		return () => {
			window.removeEventListener("message", handlePostMessage);
		};
	}, [onSuccess]);

	useEffect(() => {
		if (popupStatus !== "waiting") {
			return;
		}

		const interval = setInterval(() => {
			if (popupRef.current?.closed) {
				setPopupStatus((previousStatus) =>
					previousStatus === "waiting" ? "closed" : previousStatus,
				);
			}
		}, POPUP_CLOSED_CHECK_INTERVAL);

		return () => {
			clearInterval(interval);
		};
	}, [popupStatus]);

	return { openIntegrationPopup, popupStatus };
};
